import { CrmLead } from "@/lib/crm";

/* ------------------------------------------------------------------ */
/* Message template library (Fase 2)                                   */
/* ------------------------------------------------------------------ */

export type Channel = "whatsapp" | "email";

export interface MessageTemplate {
  id: string;
  label: string;
  /** Pipeline stages where this template is the natural next message. */
  stages: string[];
  channel: Channel | "ambos";
  subject?: string;
  body: string;
}

/**
 * Variables available in every template:
 * {primeiro_nome} {nome} {imovel} {bairro} {etapa} {faixa} {quartos}
 * {corretor} {data_visita} {consultor_whatsapp} {site}
 */
export const MESSAGE_TEMPLATES: MessageTemplate[] = [
  {
    id: "primeiro_contato",
    label: "Primeiro contato",
    stages: ["novo_lead", "primeiro_contato"],
    channel: "ambos",
    subject: "TopClass Imóveis Premium — seu contato sobre {imovel}",
    body:
      "Olá {primeiro_nome}, tudo bem? Aqui é {corretor}, da TopClass Imóveis Premium.\n\n" +
      "Recebi seu contato sobre {imovel}. Posso te enviar agora as fotos, a planta e os valores atualizados?\n\n" +
      "Se preferir, me diga o melhor horário para falarmos.",
  },
  {
    id: "qualificacao",
    label: "Qualificação (entender o perfil)",
    stages: ["primeiro_contato", "qualificacao"],
    channel: "ambos",
    subject: "Para eu selecionar os imóveis certos para você",
    body:
      "{primeiro_nome}, para eu selecionar somente o que faz sentido para você, me confirma três pontos?\n\n" +
      "1) Quantos quartos você precisa? (hoje anotei: {quartos})\n" +
      "2) Faixa de investimento? (anotei: {faixa})\n" +
      "3) Prefere {bairro} ou pode considerar bairros vizinhos?\n\n" +
      "Com isso já te mando uma seleção fechada, sem perder seu tempo.",
  },
  {
    id: "imoveis_enviados",
    label: "Envio de seleção de imóveis",
    stages: ["qualificacao", "imoveis_enviados"],
    channel: "ambos",
    subject: "Seleção de imóveis em {bairro} para você",
    body:
      "{primeiro_nome}, separei uma seleção que combina com o que conversamos em {bairro}.\n\n" +
      "Comece por este: {imovel}\n{site}\n\n" +
      "Me diz qual deles você quer conhecer primeiro que eu já reservo o horário.",
  },
  {
    id: "convite_visita",
    label: "Convite para visita",
    stages: ["imoveis_enviados", "cliente_interessado"],
    channel: "ambos",
    subject: "Podemos visitar {imovel}?",
    body:
      "{primeiro_nome}, consigo agendar a visita ao {imovel} ainda esta semana.\n\n" +
      "Tenho horários amanhã pela manhã e no fim da tarde. Qual fica melhor para você?",
  },
  {
    id: "confirmacao_visita",
    label: "Confirmação de visita",
    stages: ["visita_agendada"],
    channel: "ambos",
    subject: "Visita confirmada — {imovel}",
    body:
      "{primeiro_nome}, sua visita está confirmada.\n\n" +
      "Imóvel: {imovel}\nBairro: {bairro}\nData: {data_visita}\n\n" +
      "Vou te acompanhar pessoalmente. Qualquer imprevisto, me avise por aqui: {consultor_whatsapp}",
  },
  {
    id: "pos_visita",
    label: "Pós-visita",
    stages: ["visita_realizada"],
    channel: "ambos",
    subject: "O que achou do {imovel}?",
    body:
      "{primeiro_nome}, obrigado pela visita ao {imovel}.\n\n" +
      "O que mais te agradou e o que faltou? Com esse retorno eu ajusto a busca ou avanço na negociação do valor.",
  },
  {
    id: "proposta",
    label: "Proposta e negociação",
    stages: ["negociacao", "proposta", "contra_proposta"],
    channel: "ambos",
    subject: "Proposta — {imovel}",
    body:
      "{primeiro_nome}, levei sua proposta ao proprietário do {imovel}.\n\n" +
      "Podemos estruturar as condições de pagamento do jeito que melhor te atende. Me confirma o valor de entrada e o prazo que você considera e eu formalizo hoje.",
  },
  {
    id: "documentacao",
    label: "Documentação",
    stages: ["financiamento", "documentacao", "contrato"],
    channel: "ambos",
    subject: "Documentos para seguirmos com {imovel}",
    body:
      "{primeiro_nome}, para dar entrada no processo do {imovel} preciso destes documentos:\n\n" +
      "• RG e CPF\n• Comprovante de renda\n• Comprovante de residência\n• Certidão de estado civil\n\n" +
      "Pode enviar por aqui mesmo que eu organizo tudo.",
  },
  {
    id: "reativacao",
    label: "Reativação (lead frio)",
    stages: ["novo_lead", "primeiro_contato", "qualificacao", "imoveis_enviados", "perdido"],
    channel: "ambos",
    subject: "Novidades em {bairro} para você, {primeiro_nome}",
    body:
      "{primeiro_nome}, entraram imóveis novos em {bairro} dentro da faixa {faixa}.\n\n" +
      "Ainda faz sentido eu te enviar? Se o momento mudou, me avisa que eu guardo seu perfil para a próxima oportunidade.",
  },
  {
    id: "pos_venda",
    label: "Pós-venda e indicação",
    stages: ["escritura", "pos_venda", "cliente_fidelizado", "indicacao"],
    channel: "ambos",
    subject: "Parabéns pelo seu novo imóvel, {primeiro_nome}!",
    body:
      "{primeiro_nome}, parabéns pelo novo imóvel! Foi um prazer conduzir esse processo com você.\n\n" +
      "Se algum amigo ou familiar estiver buscando em {bairro}, pode me indicar — cuido do atendimento com a mesma dedicação.",
  },
];

const CONSULTANT_WHATSAPP = "(21) 97354-8387";
const SITE = "https://topclassimoveis.com";

export function fillTemplate(text: string, lead: CrmLead): string {
  const firstName = (lead.client_name || "").trim().split(" ")[0] || "tudo bem";
  const map: Record<string, string> = {
    "{primeiro_nome}": firstName,
    "{nome}": lead.client_name || "",
    "{imovel}": lead.property_name || "o imóvel que você procura",
    "{bairro}": lead.neighborhood || (lead.regions?.[0] ?? "Rio de Janeiro"),
    "{etapa}": lead.stage || "",
    "{faixa}": lead.price_range || "que combinamos",
    "{quartos}": lead.bedrooms || "a definir",
    "{corretor}": lead.broker || "TopClass Imóveis Premium",
    "{data_visita}": lead.visit_date
      ? new Date(lead.visit_date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
      : "a confirmar",
    "{consultor_whatsapp}": CONSULTANT_WHATSAPP,
    "{site}": SITE,
  };
  return Object.entries(map).reduce((acc, [k, v]) => acc.split(k).join(v), text);
}

/** Templates ordered with the ones matching the lead's current stage first. */
export function templatesForLead(lead: CrmLead, channel?: Channel): MessageTemplate[] {
  const list = MESSAGE_TEMPLATES.filter(
    (t) => !channel || t.channel === "ambos" || t.channel === channel,
  );
  return [...list].sort((a, b) => {
    const am = a.stages.includes(lead.stage) ? 0 : 1;
    const bm = b.stages.includes(lead.stage) ? 0 : 1;
    return am - bm;
  });
}

export function waLinkFor(lead: CrmLead, message: string): string | null {
  let digits = (lead.client_whatsapp || lead.client_phone || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length <= 11) digits = `55${digits}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export function mailLinkFor(lead: CrmLead, subject: string, body: string): string | null {
  if (!lead.client_email) return null;
  return `mailto:${lead.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
